/**
 * Creative Gallery grid — sort + infinite scroll (API or Liquid fallback).
 */
(function () {
  const Core = window.BraceletCore;
  const Cloud = window.DesignCloudApi;
  const PlazaRender = window.PlazaRender;
  if (!Core || !PlazaRender) return;

  const root = document.getElementById('creative-plaza-root');
  if (!root) return;

  const detailBase = root.dataset.detailBase || '/pages/design-detail';
  const detailSep = root.dataset.detailSep || '?';
  const useApi = root.dataset.useApi === 'true' && Cloud?.enabled?.();
  const pageSize = Math.max(1, parseInt(root.dataset.pageSize, 10) || 8);

  const els = {
    grid: root.querySelector('[data-cp-grid]'),
    empty: root.querySelector('[data-cp-empty]'),
    sentinel: root.querySelector('[data-cp-sentinel]'),
    loading: root.querySelector('[data-cp-loading]'),
    loadingMore: root.querySelector('[data-cp-loading-more]'),
    count: root.querySelector('[data-cp-count]'),
    sortBtns: root.querySelectorAll('[data-cp-sort]'),
  };

  const i18n = {
    view: root.dataset.i18nView || 'View design',
    community: root.dataset.i18nCommunity || 'Community',
    author: root.dataset.i18nAuthor || 'Community',
    count: root.dataset.i18nCount || '{{ count }} designs',
    views: root.dataset.i18nViews || 'Views',
    loadingMore: root.dataset.i18nLoadingMore || 'Loading more designs…',
  };

  const renderOpts = {
    detailBase,
    detailSep,
    viewLabel: i18n.view,
    communityLabel: i18n.community,
    authorLabel: i18n.author,
    viewsLabel: i18n.views,
  };

  const DEFAULT_SORT = 'popular';

  const state = {
    sort: DEFAULT_SORT,
    page: 1,
    totalPages: 1,
    total: 0,
    cardCount: 0,
    loading: false,
    loadingMore: false,
    liquidCache: null,
  };

  let scrollObserver = null;

  function normalizeSort(raw) {
    const s = String(raw || DEFAULT_SORT).toLowerCase();
    if (s === 'latest' || s === 'newest') return 'newest';
    if (s === 'popular' || s === 'most_popular') return 'popular';
    if (s === 'price_asc' || s === 'price-low' || s === 'price_low') return 'price_asc';
    if (s === 'price_desc' || s === 'price-high' || s === 'price_high') return 'price_desc';
    return DEFAULT_SORT;
  }

  function getPlazaBaseUrl() {
    const raw = root.dataset.plazaUrl;
    if (raw) {
      try {
        return new URL(raw, window.location.origin);
      } catch {
        /* ignore */
      }
    }
    return new URL(window.location.href);
  }

  function readSortFromUrl() {
    return normalizeSort(getPlazaBaseUrl().searchParams.get('sort'));
  }

  function syncSortToUrl(sort) {
    const url = getPlazaBaseUrl();
    if (sort && sort !== DEFAULT_SORT) url.searchParams.set('sort', sort);
    else url.searchParams.delete('sort');
    url.searchParams.delete('page');
    ['wrist_min', 'wrist_max', 'price_min', 'price_max', 'wrist_unit'].forEach((k) =>
      url.searchParams.delete(k)
    );
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function applySortFromState(sort) {
    root.dataset.activeSort = sort;
    els.sortBtns?.forEach((btn) => {
      const active = btn.dataset.cpSort === sort;
      btn.classList.toggle('creative-plaza__sort-btn--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function plazaSortCompare(a, b, sort) {
    if (sort === 'popular') {
      return (b.sort || b.viewCount || 0) - (a.sort || a.viewCount || 0);
    }
    if (sort === 'price_asc') {
      return (Number(a.price) || 0) - (Number(b.price) || 0);
    }
    if (sort === 'price_desc') {
      return (Number(b.price) || 0) - (Number(a.price) || 0);
    }
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  }

  function loadLiquidFallback() {
    const script = root.querySelector('[data-cp-liquid-fallback]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]').filter((d) => d.published && d.designJson);
    } catch {
      return [];
    }
  }

  function getLiquidSortedDesigns(sort) {
    if (!state.liquidCache) state.liquidCache = loadLiquidFallback();
    return [...state.liquidCache].sort((a, b) => plazaSortCompare(a, b, sort));
  }

  function paginateClient(designs, page) {
    const start = (page - 1) * pageSize;
    const slice = designs.slice(start, start + pageSize);
    const total = designs.length;
    return {
      designs: slice,
      pagination: {
        page,
        limit: pageSize,
        total,
        totalPages: Math.max(1, Math.ceil(total / pageSize)),
      },
    };
  }

  async function loadDesigns(page, sort) {
    const query = { page, limit: pageSize, sort };

    if (useApi) {
      try {
        return await Cloud.listPlaza(query);
      } catch (err) {
        if (Cloud.isCloudUnavailableError?.(err)) {
          console.warn('[Creative Gallery] Cloud API unavailable', err);
        } else {
          throw err;
        }
      }
    }

    const designs = getLiquidSortedDesigns(sort);
    return paginateClient(designs, page);
  }

  function updateCount(pagination) {
    if (!els.count) return;
    const total = pagination?.total ?? 0;
    els.count.textContent = i18n.count.replace('{{ count }}', String(total));
  }

  function setInitialLoading(show) {
    if (els.loading) els.loading.hidden = !show;
  }

  function setLoadingMore(show) {
    if (!els.loadingMore) return;
    if (show) {
      els.loadingMore.textContent = i18n.loadingMore;
      els.loadingMore.hidden = false;
    } else {
      els.loadingMore.hidden = true;
    }
  }

  function updateSentinel() {
    if (!els.sentinel) return;
    const hasMore = state.page < state.totalPages;
    els.sentinel.hidden = !hasMore;
    if (scrollObserver) {
      if (hasMore) scrollObserver.observe(els.sentinel);
      else scrollObserver.unobserve(els.sentinel);
    }
  }

  function setupInfiniteScroll() {
    if (!els.sentinel || scrollObserver) return;
    scrollObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (state.loading || state.loadingMore) return;
        if (state.page >= state.totalPages) return;
        void loadMore();
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 }
    );
    updateSentinel();
  }

  async function loadMore() {
    if (state.loading || state.loadingMore || state.page >= state.totalPages) return;
    state.loadingMore = true;
    setLoadingMore(true);
    try {
      const nextPage = state.page + 1;
      const { designs, pagination } = await loadDesigns(nextPage, state.sort);
      if (!designs.length) {
        state.totalPages = state.page;
        updateSentinel();
        return;
      }
      await PlazaRender.appendCards(els.grid, designs, renderOpts, state.cardCount);
      state.page = pagination?.page ?? nextPage;
      state.totalPages = pagination?.totalPages ?? state.page;
      state.total = pagination?.total ?? state.total;
      state.cardCount += designs.length;
      updateCount({ total: state.total });
      updateSentinel();
    } catch (err) {
      console.warn('[Creative Gallery] load more failed', err);
    } finally {
      state.loadingMore = false;
      setLoadingMore(false);
    }
  }

  async function resetAndLoad(sort, { scrollTop = false } = {}) {
    if (state.loading) return;
    state.loading = true;
    state.sort = sort;
    state.page = 1;
    state.cardCount = 0;
    state.liquidCache = null;
    setInitialLoading(true);
    setLoadingMore(false);
    if (els.grid) els.grid.replaceChildren();
    if (els.empty) els.empty.hidden = true;

    try {
      const { designs, pagination } = await loadDesigns(1, sort);
      state.page = pagination?.page ?? 1;
      state.totalPages = pagination?.totalPages ?? 1;
      state.total = pagination?.total ?? 0;
      state.cardCount = designs.length;

      await PlazaRender.renderCards(els.grid, designs, renderOpts);
      if (els.empty) els.empty.hidden = designs.length > 0;
      updateCount(pagination);
      updateSentinel();
      syncSortToUrl(sort);
      if (scrollTop) window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      state.loading = false;
      setInitialLoading(false);
    }
  }

  els.sortBtns?.forEach((btn) => {
    btn.addEventListener('click', () => {
      const sort = normalizeSort(btn.dataset.cpSort || DEFAULT_SORT);
      if (sort === state.sort && state.cardCount > 0) return;
      applySortFromState(sort);
      void resetAndLoad(sort, { scrollTop: true });
    });
  });

  function init() {
    const sort = readSortFromUrl();
    applySortFromState(sort);
    setupInfiniteScroll();
    void resetAndLoad(sort);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
